import { ServiceBooking } from "@turizi/shared";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useServiceBooking,
    useUpdateServiceBooking,
} from "@/hooks/api-hooks";
import { Button, Loader, NumberInput, Popover } from "@mantine/core";
import { ServiceTitle } from "../services/service-title";
import { IconX, IconAlertCircle, IconCalendar, IconRefresh, IconChevronDown, IconHistory } from "@tabler/icons-react";
import CardModal from "../card-modal";
import { Link } from "react-router-dom";
import PriceSection from "./price-section";
import CostSection from "./cost-section";
import DataSection from "./data-section";
import { RESERVATIONS_PATH, SERVICES_PATH } from "@/lib/navigation";
import { formatDayShort } from "@/lib/format-utils";
import ServiceBookingMenu from "../service-booking-menu";
import BookingServiceOrders from "./booking-service-orders";
import CoordinatorSelector from "./coordinator-selector";
import { TagIcon } from "@/lib/tag-utils";
import EditBookingAttrsSection from "./edit-booking-attrs-section";
import ProviderSelector from "./provider-selector";
import BookingPaxsPopover from "./booking-paxs-popover";
import BookingProviderVouchersSection from "./booking-provider-vouchers-section";

interface Props {
    serviceBookingId: number | string;
    opened: boolean;
    onClose: () => void;
}

export default function BookingSteps({
    serviceBookingId,
    opened,
    onClose,
}: Props) {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    const {
        data:
        bookingResponse,
        isLoading,
    } = useServiceBooking(serviceBookingId);
    const {
        mutate: updateMutation,
        isPending: saving
    } = useUpdateServiceBooking();

    const booking = bookingResponse?.data;
    const service = booking?.service_data;
    const reservation = booking?.reservation;
    const isPackage = !!service?.child_services?.length;

    const [draftAdults, setDraftAdults] = useState<number | string>(booking?.adults || '');
    const [draftChildren, setDraftChildren] = useState<number | string>(booking?.children || '');
    const [draftSeniors, setDraftSeniors] = useState<number | string>(booking?.seniors || '');
    const [draftInfants, setDraftInfants] = useState<number | string>(booking?.infants || '');
    const [draftGuests, setDraftGuests] = useState<number | string>(booking?.guests || '');

    useEffect(() => {
        if (booking) {
            setDraftAdults(booking.adults || '');
            setDraftChildren(booking.children || '');
            setDraftSeniors(booking.seniors || '');
            setDraftInfants(booking.infants || '');
            setDraftGuests(booking.guests || '');
        }
    }, [booking?.adults, booking?.children, booking?.seniors, booking?.infants, booking?.guests]);

    const isGroupCompositionChanged =
        (Number(draftAdults) || 0) !== (booking?.adults || 0) ||
        (Number(draftChildren) || 0) !== (booking?.children || 0) ||
        (Number(draftSeniors) || 0) !== (booking?.seniors || 0) ||
        (Number(draftInfants) || 0) !== (booking?.infants || 0) ||
        (Number(draftGuests) || 0) !== (booking?.guests || 0);

    const handleAccept = () => {
        if ((booking?.service_order_bookings?.length || 0) > 0) {
            if (!window.confirm(t('confirm_modify_booking_with_service_order'))) {
                return;
            }
        }
        save({
            adults: Number(draftAdults) || 0,
            children: Number(draftChildren) || 0,
            seniors: Number(draftSeniors) || 0,
            infants: Number(draftInfants) || 0,
            guests: Number(draftGuests) || 0,
        });
    };

    const save = (data: Partial<ServiceBooking>) => {
        setError(null);
        if (!booking?.id) return;
        updateMutation({ id: booking.id, ...data },
            {
                onError: (err: any) => {
                    const errorKey = err.message || 'error_updating_booking';
                    setError(`errors.${errorKey}`);
                }
            });
    };

    useEffect(() => {
        if (!opened) {
            setError(null);
        }
    }, [opened]);

    // retunrn NULL in any of these conditions:
    // - not opened
    // - not loading and not booking
    useEffect(() => {
        if ((!isLoading && !booking) || !opened) {
            onClose?.();
        }
    }, [isLoading, booking, opened]);

    if ((!isLoading && !booking) || !opened) {
        return null;
    };

    if (isLoading) {
        return (
            <CardModal
                opened={opened}
                onClose={onClose}
                footer={null}
                size="lg"
                withCloseButton={false}
            >
                <div className="flex justify-center items-center h-64">
                    <Loader color="black" />
                </div>
            </CardModal>
        );
    }

    const CardHeader = (
        <div className="space-y-2 min-w-0">
            {booking?.reservation_id && (
                <Link
                    to={`${RESERVATIONS_PATH}/${booking?.reservation_id}`}
                    className="inline-flex items-center gap-1 text-blue-700 text-xs font-medium"
                >
                    <IconCalendar size={12} className="shrink-0 text-gray-400" />
                    <span className="font-bold">#{booking?.reservation_id}</span>
                    <span>&middot;</span>
                    <span className="shrink-0">{formatDayShort(booking?.reservation?.start_day)} - {formatDayShort(booking?.reservation?.end_day)}</span>
                    <span>&middot;</span>
                    <TagIcon name={reservation?.client_business?.category} hideText noBackground size="xxs" />
                    <span className="font-bold">
                        {reservation?.client_business?.name}
                    </span>
                </Link>
            )}
            <div className="w-full relative flex items-center gap-2 sm:gap-4 justify-between min-w-0">
                <div className="flex-1 flex items-center justify-between gap-2 p-2 rounded-lg bg-gray-50 border border-gray-100 min-w-0">
                    <Link
                        to={`${SERVICES_PATH}/${service.id}`}
                        target="_blank"
                        className="text-black hover:text-blue-600 transition-colors inline-flex items-start gap-2 min-w-0 truncate"
                    >
                        <TagIcon name={booking.service_data.category} hideText size="sm" className="shrink-0" />
                        <div className="truncate text-xs sm:text-sm font-semibold">
                            <ServiceTitle service={service} hideProviders />
                        </div>
                    </Link>
                </div>
                <div className="font-bold text-gray-400 text-xs sm:text-sm shrink-0">
                    <span className="text-xxs font-normal">#</span>{booking.id}
                </div>
                <Button
                    size="xs"
                    color="gray"
                    variant="subtle"
                    radius="xl"
                    onClick={() => onClose()}
                    disabled={isLoading || saving}
                    className="hover:bg-gray-100 shrink-0"
                >
                    {(isLoading || saving) ? <Loader size="xs" color="black" /> : <IconX size={20} />}
                </Button>
            </div>
        </div>
    );

    return (
        <CardModal
            opened={opened}
            onClose={onClose}
            size="full"
            withCloseButton={false}
            footer={(
                <div className="flex items-center justify-between gap-2 w-full">
                    <ServiceBookingMenu
                        booking={booking}
                        editable={true}
                        onActionSuccess={() => onClose?.()}
                    />
                    <BookingServiceOrders booking={booking} />
                </div>
            )}
            header={CardHeader}
        >
            <div>
                {error && (
                    <div className="text-red-500 text-xs font-semibold flex items-center gap-1 mb-4">
                        <IconAlertCircle size={14} className="shrink-0" />
                        <div>{t(error)}</div>
                    </div>
                )}
                <div className="flex flex-col lg:flex-row gap-4 xl:gap-6 items-start flex-2">

                    {/* Column 2: Date, Time, Attributes & Notes */}
                    {isPackage ? (
                        <div className="space-y-2 w-full lg:flex-1">
                            {service.child_services.map(s => (
                                <div key={s.id} className="solid-box-border-tinted">
                                    <ServiceTitle service={s} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="flex-2 space-y-2">
                            <DataSection
                                booking={booking}
                                reservation={reservation}
                                save={save}
                                saving={saving}
                            />
                            <EditBookingAttrsSection
                                booking={booking}
                                onSave={() => { }} // do nothing
                            />
                        </div>
                    )}

                    {/* columsn 2 */}
                    <div >
                        <div className="solid-box-border-tinted space-y-1 w-full">
                            <div className="flex items-end flex-wrap gap-1 max-w-[200px]">
                                <NumberInput
                                    className="font-bold flex-1 min-w-[80px]"
                                    label={t('adults')}
                                    size="xs"
                                    hideControls
                                    value={draftAdults}
                                    onChange={(val) => setDraftAdults(val)}
                                />
                                <NumberInput
                                    className="font-bold flex-1 min-w-[80px]"

                                    label={t('children')}
                                    size="xs"
                                    hideControls
                                    value={draftChildren}
                                    onChange={(val) => setDraftChildren(val)}
                                />
                                <NumberInput
                                    className="font-bold flex-1 min-w-[80px]"
                                    label={booking?.service_data?.show_retired ? t('retired_plural') : t('seniors')}
                                    size="xs"
                                    hideControls
                                    value={draftSeniors}
                                    onChange={(val) => setDraftSeniors(val)}
                                />
                                <NumberInput
                                    className="font-bold flex-1 min-w-[80px]"
                                    label={t('infants')}
                                    size="xs"
                                    hideControls
                                    value={draftInfants}
                                    onChange={(val) => setDraftInfants(val)}
                                />
                                <NumberInput
                                    className="font-bold flex-1 min-w-[80px]"
                                    label={"TC"}
                                    // description={t('guests_description')}
                                    size="xs"
                                    hideControls
                                    value={draftGuests}
                                    onChange={(val) => setDraftGuests(val)}
                                />
                            </div>
                            {isGroupCompositionChanged && (
                                <Button
                                    fullWidth
                                    size="xs"
                                    color="black"
                                    radius="md"
                                    onClick={handleAccept}
                                    loading={saving}
                                    disabled={!isGroupCompositionChanged}
                                >
                                    {t('save')}
                                </Button>
                            )}
                        </div>
                        <div className="mt-1">
                            <BookingPaxsPopover
                                booking={booking}
                                reservation={reservation}
                            />
                        </div>

                        {!isPackage && (
                            <div className="space-y-2 mt-2 pt-2 border-t border-gray-200">
                                <CoordinatorSelector booking={booking} />
                                <ProviderSelector booking={booking} size="xs" />
                            </div>
                        )}

                        {/* Column 3: Cost & Price */}
                        <div className="mt-2 pt-2 border-t border-gray-200 w-full space-y-2">
                            <div>
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                                    {t('cost')}
                                </div>
                                <CostSection
                                    booking={booking}
                                    save={save}
                                    updating={saving}
                                />
                            </div>
                            <BookingProviderVouchersSection booking={booking} />
                            <div>
                                <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">
                                    {t('price')}
                                </div>
                                <PriceSection
                                    booking={booking}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </CardModal>
    );
}